import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { formatDateFromApi } from '../../utils/dateTime';
import { CategoryGroupFormUtils } from '../../utils/productCategoryGroupCommon';
import { getCategoryGroupList, getProductCountByGroupCodes, CategoryGroup, CategoryGroupListRequest, CategoryGroupTreeNode } from '../../utils/productCategoryGroup';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { 
  Menu, 
  Bell, 
  User, 
  Users, 
  CreditCard,
  Coffee,
  Gift,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Package,
  Home,
  Search,
  Filter,
  MapPin,
  Clock,
  Phone,
  Globe,
  MoreVertical,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  FolderTree,
  ChevronUp,
  ArrowUpDown,
  Grid,
  List,
  Map,
  ExternalLink
} from 'lucide-react';


// 트리 노드 개수 재귀 계산
const countTreeNodes = (nodes: CategoryGroupTreeNode[] | undefined): number => {
  if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return 0;
  return nodes.reduce((sum, node) => {
    return sum + 1 + countTreeNodes(node.sub);
  }, 0);
};

const CategoryGroupList: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-category-group-list');

  // List state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [currentPage, setCurrentPage] = useState(0); // API는 0부터 시작
  const [pageSize, setPageSize] = useState(20);

  // URL 파라미터에서 페이징 정보 읽기
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    const size = searchParams.get('size');
    
    if (page !== null) {
      setCurrentPage(parseInt(page, 10));
    }
    if (size !== null) {
      setPageSize(parseInt(size, 10));
    }
  }, [location.search]);

  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // API state
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [groupProductCounts, setGroupProductCounts] = useState<Record<string, number>>({});
  const [countsLoaded, setCountsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Check if screen is mobile size
  CategoryGroupFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = CategoryGroupFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }
      
      try {
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          return;
        }
        
        const response = await getBrandListFlat(projectCode);
        if (response && response.brands) {
          setBrands(response.brands);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        // 브랜드 목록 조회 실패해도 목록은 조회 가능하도록 에러를 무시
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  // 검색어 Debounce 처리 (500ms 지연, 최소 2글자 이상)
  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    
    // 1글자 이하일 때는 즉시 빈 문자열로 설정
    if (trimmedSearchTerm.length <= 1) {
      setDebouncedSearchTerm('');
      return;
    }
    
    // 2글자 이상일 때는 500ms 후에 debounce 처리
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // 목록 조회
  useEffect(() => {
    const fetchGroups = async () => {
      if (!projectDetail) {
        console.log('프로젝트 정보가 없습니다.');
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }
        
        const request: CategoryGroupListRequest = {
          page: currentPage,
          size: pageSize,
          sort: `${sortField},${sortOrder}`,
          keyword: debouncedSearchTerm || undefined,
          status: selectedStatus !== 'all' ? selectedStatus : undefined,
          brandCode: selectedBrand !== 'all' ? selectedBrand : undefined,
          type: selectedType !== 'all' ? selectedType : undefined
        };
        
        console.log('카테고리/그룹 목록 API 호출:', { projectCode, request });
        const response = await getCategoryGroupList(projectCode, request);
        console.log('카테고리/그룹 목록 API 응답:', response);
        
        if (response && response.productGroups) {
          setGroups(response.productGroups);
          setTotalCount(response.totalCount);
          setCountsLoaded(false);
          // API 응답의 pageSize에 맞게 pageSize 업데이트
          if (response.pageSize && response.pageSize !== pageSize) {
            setPageSize(response.pageSize);
          }
          console.log('설정된 카테고리/그룹 목록:', response.productGroups);
          console.log('설정된 총 개수:', response.totalCount);
          console.log('설정된 페이지 크기:', response.pageSize);

          const codes = response.productGroups.map((g: CategoryGroup) => g.code).filter(Boolean);
          if (codes.length > 0) {
            try {
              const counts = await getProductCountByGroupCodes(projectCode, codes);
              setGroupProductCounts(counts);
            } catch (countErr) {
              console.error('그룹별 상품 수 조회 오류:', countErr);
              setGroupProductCounts({});
            } finally {
              setCountsLoaded(true);
            }
          } else {
            setGroupProductCounts({});
            setCountsLoaded(true);
          }
        } else {
          console.warn('응답에 productGroups 데이터가 없습니다:', response);
          setGroups([]);
          setTotalCount(0);
          setGroupProductCounts({});
          setCountsLoaded(true);
        }
      } catch (err) {
        console.error('목록 조회 오류:', err);
        setError(err instanceof Error ? err.message : '목록을 불러오는 중 오류가 발생했습니다.');
        setCountsLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (projectDetail) {
      fetchGroups();
    }
  }, [projectDetail, currentPage, pageSize, sortField, sortOrder, debouncedSearchTerm, selectedStatus, selectedBrand, selectedType]);

  // Helper functions
  const getStatusBadge = (status: string) => {
    const badgeInfo = CategoryGroupFormUtils.getStatusBadge(status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeInfo.color}`}>
        <span className="w-1.5 h-1.5 mr-1 bg-current rounded-full"></span>
        {badgeInfo.text}
      </span>
    );
  };

  // 타입 라벨 (타입 셀렉트박스와 동일한 표시)
  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CATALOG: '카탈로그',
      MODULE: '화면 모듈',
      MOBILE_ORDER: '모바일 오더 카테고리',
      WEB_ORDER: '웹 오더 카테고리',
      KIOSK: '키오스크 카테고리'
    };
    return type ? (labels[type] ?? type) : '-';
  };

  const formatDate = (dateString: string) =>
    dateString ? formatDateFromApi(dateString) || dateString.split(' ')[0] : '';

  // Sort function
  const handleSort = (field: string) => {
    CategoryGroupFormUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  };

  // Select all handler
  const handleSelectAll = () => {
    CategoryGroupFormUtils.handleSelectAll(selectedGroups, filteredGroups, setSelectedGroups);
  };

  // Select group handler
  const handleSelectGroup = (groupCode: string) => {
    CategoryGroupFormUtils.handleSelectItem(groupCode, selectedGroups, setSelectedGroups);
  };

  // Page change handler
  const handlePageChange = (page: number) => {
    CategoryGroupFormUtils.handlePageChange(page, setCurrentPage, location, navigate);
  };

  // Page size change handler
  const handlePageSizeChange = (size: number) => {
    CategoryGroupFormUtils.handlePageSizeChange(size, setPageSize, setCurrentPage, location, navigate);
  };

  // 검색 조건 변경 시 페이지 리셋
  useEffect(() => {
    if (projectDetail) {
      setCurrentPage(0);
    }
  }, [debouncedSearchTerm, selectedStatus, selectedBrand, selectedType]);

  // 서버 사이드 필터링을 사용하므로 클라이언트 필터링 제거
  const filteredGroups: CategoryGroup[] = groups;

  // 프로젝트 정보가 없거나 에러가 있을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          {error ? (
            <>
              <div className="text-red-500 mb-4">
                <Home className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-red-600 mb-4 text-center max-w-md">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                새로고침
              </button>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="카테고리/그룹 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* List Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  {/* Search */}
                  <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="카테고리/그룹명, 코드로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 상태</option>
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                  </select>

                  {/* Brand Filter */}
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">전체 브랜드</option>
                    {brands.map((brand) => (
                      <option key={brand.code} value={brand.code}>
                        {brand.name}
                      </option>
                    ))}
                  </select>

                  {/* Group Filter */}
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">전체 타입</option>
                    <option value="CATALOG">카탈로그</option>
                    <option value="MODULE">화면 모듈</option>
                    <option value="MOBILE_ORDER">모바일 오더 카테고리</option>
                    {/* <option value="WEB_ORDER">웹 오더 카테고리</option>
                    <option value="KIOSK">키오스크 카테고리</option> */}
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Excel Download */}
                  <button
                    onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    엑셀
                  </button>

                  {/* Add Button */}
                  <button 
                    onClick={() => navigate('/products/category-groups/create')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    추가
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  검색결과 <span className="font-medium text-gray-900">{totalCount}</span>개
                </p>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-red-500 mb-4">
                  <Home className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            )}

            {/* Table View - 에러가 없을 때만 표시 */}
            {!error && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="pl-4 pr-0 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedGroups.length === filteredGroups.length && filteredGroups.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          브랜드
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <button
                          onClick={() => handleSort('code')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          코드
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '200px' }}>
                        <button
                          onClick={() => handleSort('name')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          이름
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          타입
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상태
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          트리수
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          상품수
                        </span>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <button
                          onClick={() => handleSort('createdAt')}
                          className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                        >
                          생성일
                          <ArrowUpDown className="ml-1 h-3 w-3" />
                        </button>
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          트리 편집
                        </span>
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">작업</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={11} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredGroups.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <Package className="w-8 h-8 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">등록된 항목이 없습니다.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredGroups.map((group) => (
                      <tr key={group.code} className="hover:bg-gray-50">
                         <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedGroups.includes(group.code)}
                            onChange={(e) => {
                              handleSelectGroup(group.code);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.brandName || group.brandCode || '-'}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.code}
                        </td>
                         <td className="pl-6 pr-0 py-4" style={{ minWidth: '200px' }}>
                           <div>
                             <div className="text-sm font-medium text-gray-900 truncate">
                               {group.title}
                             </div>
                           </div>
                         </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getTypeLabel(group.type || '')}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                          {getStatusBadge(group.status || 'ACTIVE')}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {countTreeNodes(group.tree)}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {!countsLoaded ? '-' : (groupProductCounts[group.code] ?? 0)}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.createdAt ? formatDate(group.createdAt) : '-'}
                        </td>
                        <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/products/category-groups/${group.code}/tree`)}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center text-xs font-medium"
                              title="트리 편집"
                            >
                              <FolderTree className="w-4 h-4 mr-1.5" />
                              트리 편집
                            </button>
                            <button
                              onClick={() => {
                                // tree 배열이 있고 길이가 0보다 큰 경우에만 상품 관리 페이지로 이동
                                const hasTree = (group as any).tree && Array.isArray((group as any).tree) && (group as any).tree.length > 0;
                                if (hasTree) {
                                  navigate(`/products/category-groups/${group.code}/products`);
                                } else {
                                  alert('상품 관리를 하려면 먼저 트리 편집이 필요합니다.');
                                }
                              }}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center text-xs font-medium"
                              title="상품 관리"
                            >
                              <Package className="w-4 h-4 mr-1.5" />
                              상품 관리
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              className="text-gray-400 hover:text-gray-600"
                              title="상세 보기"
                              onClick={() => alert('상세 보기 기능은 준비중입니다.')}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              className="text-gray-400 hover:text-blue-600"
                              title="수정"
                              onClick={() => navigate(`/products/category-groups/${group.code}/update`)}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="text-gray-400 hover:text-red-600"
                              title="삭제"
                              onClick={() => alert('삭제 기능은 준비중입니다.')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Selected Actions Bar */}
              {selectedGroups.length > 0 && (
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      {selectedGroups.length}개 선택됨
                    </span>
                    <div className="flex items-center space-x-2">
                      <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                        상태 변경
                      </button>
                      <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                        엑셀 다운로드
                      </button>
                      <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">
                        선택 삭제
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Pagination */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">페이지당</span>
                  <select 
                    value={pageSize}
                    onChange={(e) => {
                      handlePageSizeChange(parseInt(e.target.value));
                    }}
                    className="px-1 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10개</option>
                    <option value="20">20개</option>
                    <option value="50">50개</option>
                    <option value="100">100개</option>
                  </select>
                  <span>표시</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(0)}
                    disabled={currentPage === 0}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    처음
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  {Array.from({ length: Math.ceil(totalCount / pageSize) }, (_, i) => i).slice(0, 5).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded text-sm ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page + 1}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / pageSize) - 1, currentPage + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize) - 1)}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    마지막
                  </button>
                </div>

                <p className="text-sm text-gray-600">
                  {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} / 총 {totalCount}개
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CategoryGroupList;